import { useToken } from "@gluestack-style/react";
import { EquipmentDetail } from "components/equipment/EquipmentDetail";
import Breadcrumbs from "components/general/Breadcrumbs";
import Container from "components/general/Container";
import DismissableContent from "components/general/DismissableContent";
import PageLoader from "components/general/PageLoader";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import Seo from "components/general/Seo";
import { Box, Button, H1, HStack, ParagraphSmall, Text, Typography } from "components/ui";
import { Link, useLocalSearchParams } from "expo-router";
import { Calendar, Tractor, TriangleAlertIcon } from "lucide-react-native";
import React, { useMemo, useEffect } from "react";
import { useGetEquipmentByIdQuery, usePostEquipmentByIdLogMutation } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";

export function EquipmentScreen() {
	const { id, source } = useLocalSearchParams<{ id: string; source?: string }>();
	const isMobile = useIsMobile();
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const resolvedPrimarylightBlue = useToken("colors", "primarylightblue");
	const resolvedWarningMain = useToken("colors", "warningmain");
	const {
		data: equipment,
		isLoading,
		isFetching,
	} = useGetEquipmentByIdQuery({
		id: id,
	});

	const [postLog] = usePostEquipmentByIdLogMutation();

	const isOverdueForService = useMemo(() => {
		if (equipment && equipment.nextServiceDueDate) {
			const today = new Date();
			const nextServiceDueDate = new Date(equipment.nextServiceDueDate);
			return today > nextServiceDueDate;
		}
		return false;
	}, [equipment]);

	function sendVisit(data: Record<string, any>) {
		postLog({
			assetLoggingEvent: {
				latitude: data.latitude,
				longitude: data.longitude,
			},
			id: Number(id),
		});
	}

	useEffect(() => {
		if (source != "qrcode") {
			return;
		}

		if (typeof window === "undefined" || typeof navigator === "undefined") return;

		const payload: Record<string, any> = {
			page: `/equipment/my-equipment/${id}`,
			source,
			timestamp: new Date().toISOString(),
		};

		let didRespond = false;

		// Fallback if user ignores geolocation
		const fallbackTimeout = setTimeout(() => {
			if (!didRespond) {
				sendVisit({ ...payload, locationIgnored: true });
			}
		}, 12000);

		if ("geolocation" in navigator) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					didRespond = true;
					clearTimeout(fallbackTimeout);

					const { latitude, longitude } = position.coords;
					sendVisit({ ...payload, latitude, longitude });
				},
				(error) => {
					didRespond = true;
					clearTimeout(fallbackTimeout);
					console.warn("Geolocation denied or failed:", error.message);
					sendVisit({ ...payload, locationDenied: true, error: error.message });
				},
				{
					enableHighAccuracy: false,
					timeout: 10000, // Still useful if user does *interact*
					maximumAge: 60000,
				},
			);
		} else {
			console.warn("Geolocation not supported");
			sendVisit({ ...payload, geolocationUnsupported: true });
		}

		return () => clearTimeout(fallbackTimeout); // clean up on unmount
	}, [id, source]);

	if (isLoading || isFetching) {
		return (
			<Box minHeight={150}>
				<PageLoader />
			</Box>
		);
	}
	let equipmentName = `${equipment?.make} ${equipment?.model}`;

	const isPublicView = !equipment?.id || equipment.id === 0;

	return (
		<ScrollContainer>
			<Seo title={equipmentName} description={`Details for ${equipmentName}`} />

			<Container>
				<Box
					sx={{
						paddingHorizontal: 16,
						"@md": {
							paddingHorizontal: 48,
						},
					}}
					paddingVertical={32}
				>
					<Box pb={20}>
						<Breadcrumbs idReplacement={equipmentName} />
					</Box>
					<HStack borderBottomWidth={1} borderBottomColor="$neutral50" flexDirection={isMobile ? "column" : "row"} justifyContent="center" gap={16} mb={24} pb={24}>
						<Tractor size={48} color={resolvedPrimarylightBlue} />
						<H1
							sx={
								{
									// paddingBottom: 16,
									// "@md": {
									// 	paddingBottom: 20,
									// 	lineHeight: 48,
									// },
								}
							}
						>
							{equipmentName}
						</H1>
						{!isPublicView && (
							<HStack alignItems="center" marginLeft={isMobile ? 0 : "auto"} gap={16}>
								{/* <Box flex={isMobile ? 1 : undefined}>
								<Link href={`/equipment/my-equipment/${id}/order-parts`}>
									<Button width={"$full"} borderRadius={12} gap={4} borderColor="transparent" variant="outline" bg="$primarylight">
										<Calendar color="#ffffff" />
										<ParagraphSmall
											sx={{
												color: "$basicwhite",
											}}
										>
											Order Parts
										</ParagraphSmall>
									</Button>
								</Link>
							</Box> */}
								<Box flex={isMobile ? 1 : undefined}>
									<Link href={`/equipment/my-equipment/book-a-service`} asChild>
										<Button paddingHorizontal={24} paddingVertical={12} width={"$full"} borderRadius={12} gap={4} borderWidth={1} borderColor="$primarylight" bg="transparent">
											<Calendar color={resolvedPrimarylight} />
											<Text color="$primarylight">Book a service</Text>
										</Button>
									</Link>
								</Box>
							</HStack>
						)}
					</HStack>
					{!isPublicView && isOverdueForService && (
						<DismissableContent icon={<TriangleAlertIcon size={24} color={resolvedWarningMain} />} sx={{ backgroundColor: "white" }} variant="warning">
							<Typography weight="bold"> Note: This machine is overdue for a service</Typography>
						</DismissableContent>
					)}
				</Box>
			</Container>
			<Section borderRadius={0} flexDirection="row">
				<Container>
					<EquipmentDetail data={equipment} />
				</Container>
			</Section>
		</ScrollContainer>
	);
}
